import { useEffect, useState } from 'preact/hooks';
import { loadMember, type Grid, type Mark, type MemberDoc } from '../data';
import { afterElection, CONTACT_FORMER, fmtLong, leaderWords, ordinal } from '../format';
import { cls, officeDay, passedHow, trialWord } from '../grid/Grid';
import { GLYPH, JARGON } from '../grid/marks';
import { navigate } from '../router';
import { NON_STATES, STATES } from './Chrome';
import { useOverlay } from './ItemModal';
import { ShareButton, SharePanel, memberShare, pageTitle, type ShareWhat } from './Share';
import './overlay.css';

// The member card (README, "Member card"): a 460px panel docked right. It separates years
// actually served from years since service began, so the two can be checked against each
// other, and its vote list is the row in words — the screen-reader form of the grid.

const OFFICE: Record<string, string> = { rep: 'House', sen: 'Senate', prez: 'President', viceprez: 'Vice President' };
const ROLE: Record<string, string> = { rep: 'House', sen: 'Senate', president: 'President', vp: 'Vice President' };
// Whole years between two ISO dates, by the calendar: the anniversaries passed. Dividing the
// days by 365.2425 undercounts a span with fewer leap days than the average (the worker's
// served_years says why), so both figures on the card are counted this way.
export const wholeYears = (from: string, to: string) =>
  +to.slice(0, 4) - +from.slice(0, 4) - (to.slice(5) < from.slice(5) ? 1 : 0);

export const yrs = (n: number) => `${n} ${n === 1 ? 'yr' : 'yrs'}`;
export const placeName = (state: string) => {
  const name = STATES[state] ?? NON_STATES[state];
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : null;
};
const place = (state: string | null, district: number | null) =>
  !state ? '' : placeName(state) ? placeName(state) + (district == null ? '' : district === 0 ? ', at large' : `, District ${district}`) : state;

/** Consecutive terms in the same seat, joined: "House · Iowa, District 3 · 1975–1981". */
export function history(doc: MemberDoc, asOf: string) {
  const out: { what: string; when: string }[] = [];
  let run: { office: string; where: string; start: string; end: string; open?: boolean } | null = null;
  const flush = () => {
    if (!run) return;
    // held_open: an appointment past its special election, the winner not yet seated (build_grid.mark_held_open)
    const ongoing = run.end > asOf || !!run.open;
    out.push({ what: [OFFICE[run.office] ?? run.office, run.where].filter(Boolean).join(' · '),
               when: `${run.start.slice(0, 4)}–${ongoing ? 'now' : run.end.slice(0, 4)}` });
  };
  for (const t of doc.terms) {
    const where = place(t.state, t.district);
    const joins = run && run.office === t.office && run.where === where
      // a new Congress can swear members in a few days after the last term ended;
      // a real break in service is at least a whole term
      && (Date.parse(t.start) - Date.parse(run.end)) <= 60 * 86400000;
    if (run && joins) { run.end = t.end; run.open = t.held_open; continue; }
    flush();
    run = { office: t.office, where, start: t.start, end: t.end, open: t.held_open };
  }
  flush();
  return out.reverse();
}

type Load = { state: 'loading' } | { state: 'failed'; why: string } | { state: 'loaded'; doc: MemberDoc };

export function MemberCard({ id, grid, onClose }: { id: string; grid: Grid; onClose: () => void }) {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const box = useOverlay(onClose);
  useEffect(() => {
    setLoad({ state: 'loading' });
    loadMember(id).then((doc) => setLoad({ state: 'loaded', doc }))
      .catch((e) => setLoad({ state: 'failed', why: String(e.message ?? e) }));
  }, [id]);
  useEffect(() => { if (load.state !== 'loading') pageTitle(load.state === 'loaded' ? load.doc.full : null); }, [load]);

  return (
    <div class="sr-card-backdrop" onClick={onClose}>
      <div ref={box} class="sr-card" role="dialog" aria-modal="true" aria-label="Member" tabIndex={-1}
           onClick={(e) => e.stopPropagation()}>
        {/* A phone shows this as a full-screen sheet with a way back at the top (README §11). */}
        <button class="btn btn-ghost sr-sheet-back" onClick={onClose}>← Back</button>
        {load.state === 'loading' && <div class="sr-overlay-state sr-muted">Loading the member…</div>}
        {load.state === 'failed' && (
          <div class="sr-overlay-state">
            <p>This member could not be loaded (<code>{load.why}</code>).</p>
            <button class="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}
        {load.state === 'loaded' && <CardBody doc={load.doc} grid={grid} onClose={onClose} />}
      </div>
    </div>
  );
}

function CardBody({ doc, grid, onClose }: { doc: MemberDoc; grid: Grid; onClose: () => void }) {
  const here = doc.memberships.filter((m) => m.congress === grid.congress)
    .sort((a, b) => (a.from < b.from ? 1 : -1));
  const m = here[0];                                 // the latest role held in this Congress
  const rowIdx = grid.rows.map((r, i) => (r.id === doc.id ? i : -1)).filter((i) => i >= 0);
  const isSpeaker = rowIdx.some((r) => grid.cells[r].includes('s'));
  const delegate = grid.delegates.some((d) => d.id === doc.id);
  const asOf = grid.as_of;
  const [share, setShare] = useState(false);
  useEffect(() => setShare(false), [doc.id]);
  const shareProps = { share, setShare, what: memberShare(doc, m) };

  if (!m) {
    return (
      <>
        <Head doc={doc} sub="" onClose={onClose} {...shareProps} />
        <div class="sr-card-sec">
          <p>Not a member in the {ordinal(grid.congress)} Congress. Served in:{' '}
            {doc.memberships.map((x) => ordinal(x.congress)).join(', ') || 'none published yet'}.</p>
        </div>
      </>
    );
  }

  const sub = [m.party, place(m.seat?.split('-')[0] ?? null, null) || null,
    m.seat?.includes('-') ? (m.seat.endsWith('-AL') ? 'At large' : `District ${+m.seat.split('-')[1]}`) : null,
    ROLE[m.role] ?? m.role, leaderWords(m.leader)].filter(Boolean).join(' · ');
  // Counted to the day the person left, if they have: counted to today, Rubio read 15 years
  // since service began beside 14 served, a one-year gap in service that never happened.
  const sinceFirst = doc.first_sworn ? wholeYears(doc.first_sworn, m.left ? m.to : asOf) : null;
  const pct = Math.max(2, Math.round((m.tenure_days / 365.2425 / grid.tenure_scale_years) * 100));
  const current = doc.terms.find((t) => t.start <= asOf && asOf < t.end);

  // Every item this term with this person's mark, newest first. One person can hold two
  // rows in a Congress (Vance: Senate, then Vice President); each item takes whichever
  // row carries a real mark.
  // Newest first by this person's OWN office's day, with its year (12 Oct 2026 audit: Grassley's read
  // "S. 2403 · Unanimous consent · 9 Oct" among September 2026 votes; it was 9 Oct 2025).
  const votes: { id: string; title: string; mark: Mark; meta: string; day: string | null }[] = [];
  let skipped = 0;
  grid.columns.forEach((col, c) => {
    const marks = rowIdx.map((r) => grid.cells[r][c] as Mark);
    const k = marks.findIndex((x) => x !== 'x' && x !== 'o');
    if (k < 0) { skipped++; return; }
    const mark = marks[k], role = grid.rows[rowIdx[k]].role, day = officeDay(col, role, mark);
    const label = mark === '.' && m.role === 'president' ? 'Awaiting action'
      : trialWord(col, role, mark) ?? passedHow(col, role, mark) ?? grid.legend[mark] ?? mark;
    votes.push({ id: col.id, title: `${col.number} · ${col.title ?? ''}`, mark, day,
                 meta: day ? `${label} · ${fmtLong(day)}` : label });
  });
  votes.sort((x, y) => (y.day ?? '').localeCompare(x.day ?? ''));   // stable: same day, grid order

  const a = m.attendance;
  const missed = a && a.eligible ? `${a.missed} of ${a.eligible} · ${((a.missed / a.eligible) * 100).toFixed(1)}%` : '—';
  const noVote = m.role === 'president' ? 'The President does not vote on roll calls.'
    : m.role === 'vp' ? 'The Vice President votes only to break a tie in the Senate.' : null;

  return (
    <>
      <Head doc={doc} sub={sub} onClose={onClose} {...shareProps} />

      <div class="sr-card-sec">
        <div class="sr-tenure-top sr-ui">
          <span class="sr-kicker">Time in federal office</span>
          <span>{yrs(m.years)}</span>
        </div>
        <div class="sr-tenure-track"><div class="sr-tenure-fill" style={{ width: pct + '%' }} /></div>
        <div class="sr-history sr-ui">
          {/* "Service began", not "first sworn in": the date follows the Biographical Directory,
              which dates Pallone's service from a special election before any oath was taken. */}
          {doc.first_sworn && <div><span>Service began</span><span>{fmtLong(doc.first_sworn)}</span></div>}
          <div><span>Years actually served</span><span>{yrs(m.years)}</span></div>
          {sinceFirst != null && <div><span>Years since service began</span><span>{yrs(sinceFirst)}</span></div>}
          {m.left
            ? <div><span>Left office</span><span>{fmtLong(m.to)}</span></div>
            : current && <div><span>Current term ends</span><span>{fmtLong(current.end)}</span></div>}
        </div>
        <div class="sr-history sr-history-sub sr-ui">
          <div class="sr-kicker" style={{ border: 0 }}>Terms in office</div>
          {history(doc, asOf).map((h, i) => <div key={i}><span>{h.what}</span><span>{h.when}</span></div>)}
        </div>
      </div>

      <div class="sr-card-sec sr-facts sr-ui">
        <div>
          {/* The White House seat is elected in the presidential election; "Next election"
              on a President's card could read as the person being on the ballot, which the
              22nd Amendment can rule out (Dan, 21 Sep 2026). */}
          <div class="sr-kicker">{afterElection(m)?.[0] ?? (m.role === 'president' || m.role === 'vp' ? 'Next presidential election' : 'Next election')}</div>
          <div class="sr-fact">{afterElection(m)?.[1] ?? m.next_election ?? '—'}</div>
        </div>
        <div>
          <div class="sr-kicker">Roll calls missed</div>
          <div class="sr-fact">{noVote ? '—' : missed}</div>
          {noVote && <div class="sr-fact-note">{noVote}</div>}
          {!noVote && a && a.not_listed > 0 && (
            <div class="sr-fact-note">
              Not listed on {a.not_listed} more. {isSpeaker ? JARGON.speaker : 'The official record does not list them on those roll calls.'}
            </div>
          )}
          {!noVote && a && <div class="sr-fact-note">All roll calls in the {ordinal(grid.congress)} Congress, not only final votes.</div>}
        </div>
      </div>

      <div class="sr-card-sec sr-contact sr-ui">
        <div class="sr-kicker">Contact</div>
        {doc.contact.phone && <div>{doc.contact.phone}</div>}
        {doc.contact.form && <div><a href={doc.contact.form} rel="noopener">Official contact form</a></div>}
        {!doc.contact.phone && !doc.contact.form && (
          <div class="sr-muted">{doc.contact.former ? CONTACT_FORMER : 'No office contact in the record.'}</div>
        )}
      </div>

      <div class="sr-card-sec">
        <div class="sr-kicker">Votes this term · newest first</div>
        <div role="list">
          {votes.map((v) => (
            <button key={v.id} role="listitem" class="sr-vote" onClick={() => navigate('/item/' + v.id)}>
              <span class={'sr-cell c-' + cls(v.mark)} aria-hidden="true">{GLYPH[v.mark]}</span>
              <span class="sr-vote-title">{v.title}</span>
              <span class="sr-ui">{v.meta}</span>
            </button>
          ))}
        </div>
        {delegate ? (
          <div class="sr-share">
            The member of the House for {placeName(m.seat?.split('-')[0] ?? '') ?? m.seat} cannot vote on final passage,
            so has no vote on any of this term’s {grid.columns.length} items and is not in the grid.
          </div>
        ) : skipped > 0 && (
          <div class="sr-share">{skipped} other item{skipped === 1 ? '' : 's'} this term did not involve this office, or came while they were not in it.</div>
        )}
        <div class="sr-share">Shareable link: votesonrecord.org/member/{doc.id}</div>
      </div>
    </>
  );
}

function Head({ doc, sub, onClose, share, setShare, what }: {
  doc: MemberDoc; sub: string; onClose: () => void; share: boolean; setShare: (b: boolean) => void; what: ShareWhat;
}) {
  return (
    <>
      <div class="sr-card-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{doc.full}</h3>
          <div class="sr-ui">{sub}</div>
        </div>
        <ShareButton open={share} onToggle={() => setShare(!share)} controls="sr-share-member" />
        <button class="btn btn-ghost sr-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      {share && <SharePanel id="sr-share-member" what={what} />}
    </>
  );
}
